// Dependency-free WebGL particle field behind the landing page hero + story.
// One draw call of N points; every particle carries four target positions
// (see shapes.js) and the vertex shader morphs between them on `uMorph`
// (0 → 3, driven by scroll). All motion — the hero wave, the mid-morph swirl,
// pointer repulsion — happens on the GPU, so the JS side only updates a
// handful of uniforms per frame.
import { buildShapes } from './shapes';

const VERT = `
precision highp float;
attribute vec3 aSheet;
attribute vec3 aCloud;
attribute vec3 aGlyph;
attribute vec3 aEnv;
attribute vec4 aSeed;

uniform mat4 uProj;
uniform float uCamZ;
uniform float uTime;
uniform float uMorph;
uniform float uIntro;
uniform float uPixelRatio;
uniform float uSize;
uniform float uScale;
uniform vec2 uOffset;
uniform vec2 uRot;
uniform vec3 uMouse;
uniform vec3 uBrand;
uniform vec3 uBrandLight;
uniform vec3 uChaos1;
uniform vec3 uChaos2;
uniform vec3 uChaos3;
uniform vec3 uChaos4;

varying vec3 vColor;
varying float vAlpha;

float easeIO(float t) {
  return t < 0.5 ? 4.0 * t * t * t : 1.0 - pow(-2.0 * t + 2.0, 3.0) / 2.0;
}

vec3 rotate(vec3 p, vec2 r) {
  float cy = cos(r.y), sy = sin(r.y);
  p = vec3(p.x * cy + p.z * sy, p.y, -p.x * sy + p.z * cy);
  float cx = cos(r.x), sx = sin(r.x);
  return vec3(p.x, p.y * cx - p.z * sx, p.y * sx + p.z * cx);
}

// xyz = position, w = wave height (used to light the crests)
vec4 sheetPos(vec3 p, float t) {
  float y = sin(p.x * 0.55 + t * 0.55) * 0.38
          + sin(p.z * 1.15 - t * 0.42 + p.x * 0.3) * 0.26
          + sin((p.x + p.z) * 1.9 + t * 0.9) * 0.07;
  vec3 q = vec3(p.x, y, p.z - 0.5);
  float c = cos(0.32), s = sin(0.32);
  q = vec3(q.x, q.y * c - q.z * s, q.y * s + q.z * c);
  q.y -= 1.25;
  return vec4(q, y);
}

void main() {
  float m = clamp(uMorph, 0.0, 3.0);
  float seg = min(floor(m), 2.0);
  float t = m - seg;
  float st = easeIO(clamp((t - aSeed.x * 0.3) / 0.7, 0.0, 1.0));

  vec2 spin = uRot + vec2(0.0, uTime * 0.12);
  vec3 off = vec3(uOffset, 0.0);
  vec4 sw = sheetPos(aSheet, uTime);
  vec3 s0 = sw.xyz;
  vec3 s1 = rotate(aCloud * uScale, spin) + off;
  vec3 s2 = rotate(aGlyph * uScale, uRot) + off;
  vec3 s3 = rotate(aEnv * uScale, uRot) + off;

  vec3 a = seg < 0.5 ? s0 : (seg < 1.5 ? s1 : s2);
  vec3 b = seg < 0.5 ? s1 : (seg < 1.5 ? s2 : s3);
  vec3 p = mix(a, b, st);

  // Particles arc outward mid-transition instead of sliding in straight lines.
  p += sin(st * 3.14159) * (aSeed.wzy - 0.5) * 1.1;

  float chaosW = 1.0 - clamp(abs(m - 1.0) * 1.15, 0.0, 1.0);
  float ph = aSeed.w * 6.2831;
  p += vec3(sin(uTime * 0.8 + ph), cos(uTime * 0.7 + ph * 1.3), sin(uTime * 0.6 + ph * 0.7))
       * (0.012 + 0.09 * chaosW);

  float it = easeIO(clamp((uIntro - aSeed.x * 0.45) / 0.55, 0.0, 1.0));
  p = mix(aCloud * 3.4 + vec3(0.0, 0.0, -3.0), p, it);

  vec2 d = p.xy - uMouse.xy;
  float f = (1.0 - smoothstep(0.0, 1.15, length(d))) * uMouse.z;
  p.xy += normalize(d + 0.0001) * f * 0.5;
  p.z += f * 0.35;

  vec4 mv = vec4(p.x, p.y, p.z - uCamZ, 1.0);
  gl_Position = uProj * mv;

  float sparkle = step(0.955, aSeed.z);
  gl_PointSize = max(1.0, uSize * uPixelRatio * (0.6 + aSeed.y * 0.9) * (1.0 + sparkle * 0.9) * (6.0 / -mv.z));

  vec3 base = mix(uBrand, uBrandLight, smoothstep(0.05, 0.9, aSeed.z));
  base = mix(base, vec3(1.0), sparkle);
  vec3 chaosCol = aSeed.z < 0.25 ? uChaos1 : (aSeed.z < 0.5 ? uChaos2 : (aSeed.z < 0.75 ? uChaos3 : uChaos4));
  vColor = mix(base, chaosCol, chaosW * 0.85);

  float sheetW = 1.0 - clamp(m, 0.0, 1.0);
  float crest = smoothstep(-0.35, 0.55, sw.w);
  vColor = mix(vColor, mix(vColor, uBrandLight + 0.25, crest * 0.6), sheetW);
  float sheetLight = smoothstep(-10.5, -6.5, mv.z) * (0.45 + 0.75 * crest);
  vAlpha = (0.62 + 0.38 * aSeed.y) * mix(1.0, sheetLight, sheetW) * it;
}
`;

const FRAG = `
precision mediump float;
varying vec3 vColor;
varying float vAlpha;
void main() {
  vec2 c = gl_PointCoord - 0.5;
  float a = 1.0 - smoothstep(0.0, 0.5, length(c));
  a *= a * vAlpha;
  gl_FragColor = vec4(vColor * a, a);
}
`;

const FOV = (42 * Math.PI) / 180;
const CAM_Z = 6;

function hexToRgb(hex, fallback) {
  const m = /^#?([0-9a-f]{6})$/i.exec((hex || '').trim());
  if (!m) return fallback;
  const n = parseInt(m[1], 16);
  return [((n >> 16) & 255) / 255, ((n >> 8) & 255) / 255, (n & 255) / 255];
}

function cssColor(name, fallback) {
  try {
    return hexToRgb(getComputedStyle(document.documentElement).getPropertyValue(name), fallback);
  } catch (e) {
    return fallback;
  }
}

function perspective(fov, aspect, near, far) {
  const f = 1 / Math.tan(fov / 2);
  const nf = 1 / (near - far);
  return new Float32Array([
    f / aspect, 0, 0, 0,
    0, f, 0, 0,
    0, 0, (far + near) * nf, -1,
    0, 0, 2 * far * near * nf, 0,
  ]);
}

function compile(gl, type, src) {
  const sh = gl.createShader(type);
  gl.shaderSource(sh, src);
  gl.compileShader(sh);
  if (!gl.getShaderParameter(sh, gl.COMPILE_STATUS)) {
    const log = gl.getShaderInfoLog(sh);
    gl.deleteShader(sh);
    throw new Error(log || 'shader compile failed');
  }
  return sh;
}

export function isWebGLAvailable() {
  try {
    const c = document.createElement('canvas');
    return !!(window.WebGLRenderingContext && (c.getContext('webgl') || c.getContext('experimental-webgl')));
  } catch (e) {
    return false;
  }
}

export class InkField {
  // portrait: where the shapes sit on tall screens — y = offset above centre
  // (fraction of half-height), fit = width they may take (fraction of half-width).
  constructor(canvas, { reducedMotion = false, portrait = { y: 0.3, fit: 0.88 } } = {}) {
    this.canvas = canvas;
    this.reducedMotion = reducedMotion;
    this.portrait = portrait;
    const gl = canvas.getContext('webgl', { antialias: false, alpha: true, premultipliedAlpha: true, powerPreference: 'high-performance' });
    if (!gl) throw new Error('webgl unavailable');
    this.gl = gl;

    const small = Math.min(window.innerWidth, window.innerHeight) < 700;
    this.count = small ? 7000 : 16000;
    this.pixelRatio = Math.min(window.devicePixelRatio || 1, small ? 1.6 : 2);

    const prog = gl.createProgram();
    gl.attachShader(prog, compile(gl, gl.VERTEX_SHADER, VERT));
    gl.attachShader(prog, compile(gl, gl.FRAGMENT_SHADER, FRAG));
    gl.linkProgram(prog);
    if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) throw new Error(gl.getProgramInfoLog(prog) || 'link failed');
    gl.useProgram(prog);
    this.prog = prog;

    const shapes = buildShapes(this.count);
    this.buffers = [];
    const attr = (name, data, size) => {
      const buf = gl.createBuffer();
      gl.bindBuffer(gl.ARRAY_BUFFER, buf);
      gl.bufferData(gl.ARRAY_BUFFER, data, gl.STATIC_DRAW);
      const loc = gl.getAttribLocation(prog, name);
      gl.enableVertexAttribArray(loc);
      gl.vertexAttribPointer(loc, size, gl.FLOAT, false, 0, 0);
      this.buffers.push(buf);
    };
    attr('aSheet', shapes.sheet, 3);
    attr('aCloud', shapes.cloud, 3);
    attr('aGlyph', shapes.glyph, 3);
    attr('aEnv', shapes.envelope, 3);
    attr('aSeed', shapes.seeds, 4);

    this.u = {};
    ['uProj', 'uCamZ', 'uTime', 'uMorph', 'uIntro', 'uPixelRatio', 'uSize', 'uScale', 'uOffset', 'uRot', 'uMouse',
      'uBrand', 'uBrandLight', 'uChaos1', 'uChaos2', 'uChaos3', 'uChaos4'].forEach((n) => {
      this.u[n] = gl.getUniformLocation(prog, n);
    });

    gl.uniform3fv(this.u.uBrand, cssColor('--color-primary-700', [0.055, 0.133, 0.94]));
    gl.uniform3fv(this.u.uBrandLight, cssColor('--color-accent-on-dark', [0.56, 0.63, 1]));
    gl.uniform3fv(this.u.uChaos1, cssColor('--color-warning-500', [0.96, 0.62, 0.04]));
    gl.uniform3fv(this.u.uChaos2, cssColor('--color-danger-500', [0.9, 0.28, 0.3]));
    gl.uniform3fv(this.u.uChaos3, cssColor('--color-success-500', [0.07, 0.72, 0.42]));
    gl.uniform3fv(this.u.uChaos4, cssColor('--color-accent-purple-500', [0.55, 0.36, 0.96]));
    gl.uniform1f(this.u.uCamZ, CAM_Z);
    gl.uniform1f(this.u.uSize, small ? 3.0 : 2.8);

    gl.disable(gl.DEPTH_TEST);
    gl.enable(gl.BLEND);
    gl.blendFunc(gl.ONE, gl.ONE);
    gl.clearColor(0, 0, 0, 0);

    this.morph = 0;
    this.morphTarget = 0;
    this.intro = reducedMotion ? 1 : 0;
    this.time = 0;
    this.pointer = { x: 0, y: 0, tx: 0, ty: 0, s: 0, ts: 0 };
    this.running = false;
    this.last = 0;
    this.frame = this.frame.bind(this);

    this.onLost = (e) => {
      e.preventDefault();
      this.stop();
    };
    canvas.addEventListener('webglcontextlost', this.onLost);
    this.resize();
  }

  resize() {
    const { canvas, gl } = this;
    const w = canvas.clientWidth || window.innerWidth;
    const h = canvas.clientHeight || window.innerHeight;
    canvas.width = Math.round(w * this.pixelRatio);
    canvas.height = Math.round(h * this.pixelRatio);
    gl.viewport(0, 0, canvas.width, canvas.height);
    const aspect = w / h;
    gl.uniformMatrix4fv(this.u.uProj, false, perspective(FOV, aspect, 0.1, 50));
    gl.uniform1f(this.u.uPixelRatio, this.pixelRatio);

    this.halfH = Math.tan(FOV / 2) * CAM_Z;
    this.halfW = this.halfH * aspect;
    // Desktop: shapes sit right of the story copy. Portrait: centred, lifted
    // above the copy, shrunk to fit the narrow width.
    if (aspect > 1.05) {
      gl.uniform2f(this.u.uOffset, this.halfW * 0.4, 0.05);
      gl.uniform1f(this.u.uScale, Math.min(1, (this.halfH * 0.86) / 1.9));
    } else {
      gl.uniform2f(this.u.uOffset, 0, this.halfH * this.portrait.y);
      gl.uniform1f(this.u.uScale, Math.min(0.9, (this.halfW * this.portrait.fit) / 1.95));
    }
    if (!this.running) this.render();
  }

  setMorph(v) {
    this.morphTarget = v;
    if (!this.running) {
      this.morph = v;
      this.render();
    }
  }

  setPointer(nx, ny) {
    this.pointer.tx = nx;
    this.pointer.ty = ny;
    this.pointer.ts = 1;
    clearTimeout(this.idleTimer);
    this.idleTimer = setTimeout(() => { this.pointer.ts = 0; }, 1400);
  }

  start() {
    if (this.running) return;
    this.running = true;
    this.last = performance.now();
    this.raf = requestAnimationFrame(this.frame);
  }

  stop() {
    this.running = false;
    cancelAnimationFrame(this.raf);
  }

  frame(now) {
    if (!this.running) return;
    const dt = Math.min(0.05, (now - this.last) / 1000);
    this.last = now;
    const k = (rate) => 1 - Math.pow(1 - rate, dt * 60);

    if (!this.reducedMotion) {
      this.time += dt;
      this.intro = Math.min(1, this.intro + dt / 2.6);
    }
    this.morph += (this.morphTarget - this.morph) * k(0.07);
    const p = this.pointer;
    p.x += (p.tx - p.x) * k(0.08);
    p.y += (p.ty - p.y) * k(0.08);
    p.s += (p.ts - p.s) * k(0.05);

    this.render();
    this.raf = requestAnimationFrame(this.frame);
  }

  render() {
    const { gl, u, pointer: p } = this;
    gl.uniform1f(u.uTime, this.time);
    gl.uniform1f(u.uMorph, this.morph);
    gl.uniform1f(u.uIntro, this.intro);
    const sway = this.reducedMotion ? 0 : Math.sin(this.time * 0.35) * 0.16;
    gl.uniform2f(u.uRot, p.y * -0.18, sway + p.x * 0.32);
    gl.uniform3f(u.uMouse, p.x * this.halfW, p.y * this.halfH, this.reducedMotion ? 0 : p.s);
    gl.clear(gl.COLOR_BUFFER_BIT);
    gl.drawArrays(gl.POINTS, 0, this.count);
  }

  destroy() {
    this.stop();
    clearTimeout(this.idleTimer);
    this.canvas.removeEventListener('webglcontextlost', this.onLost);
    const { gl } = this;
    this.buffers.forEach((b) => gl.deleteBuffer(b));
    gl.deleteProgram(this.prog);
    const ext = gl.getExtension('WEBGL_lose_context');
    if (ext) ext.loseContext();
  }
}
